import { reaction } from 'mobx';
import { PopupHostBridge } from '@cef-ai/wallet';
import type { ApiClient } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import { getFirstPartyOrigins } from '~/config';
import {
  PendingConsentStore,
  PendingRegistrationStore,
  type AppContextStore,
  type ApplicationsStore,
  type DelegationsStore,
} from '~/stores';
import {
  createHelloHandler,
  createSignHandler,
  createFindApplicationsHandler,
  createSaveApplicationHandler,
  createRequestDelegationHandler,
  createLogoutHandler,
  createSessionStatusHandler,
} from '~/popup/handlers';

export interface CreatePopupBridgeOptions {
  /** Origin of the host page (read from the popup URL's `?origin=...` query). */
  hostOrigin: string;
  /** Optional window override for tests. Defaults to `globalThis.window`. */
  popupWindow?: Window;
  identity: Identity;
  apiClient: ApiClient;
  appContextStore: AppContextStore;
  applicationsStore: ApplicationsStore;
  delegationsStore: DelegationsStore;
  /**
   * Optional injection point — production callers can omit this and the
   * bridge constructs its own. Tests pass a pre-built one to inspect/drive.
   */
  pendingConsentStore?: PendingConsentStore;
  /**
   * Task 22. Same injection point as `pendingConsentStore`, for the embedded
   * registration prompt: production callers omit it and the bridge constructs
   * its own; tests pass a pre-built one to drive.
   */
  pendingRegistrationStore?: PendingRegistrationStore;
  /**
   * Issue #36. The route's boot-time silent-resume attempt, hoisted out of its
   * `useEffect` so the `wallet:session:status` handler can await it before
   * answering — see `createSessionStatusHandler`'s `bootResume` doc.
   *
   * Wired here as well as in `createIframeBridge` because the race is not
   * iframe-specific: `EmbedWalletRoute`'s boot-resume effect runs on the popup
   * surface too, and `EmbedWallet.login()` runs the same resume-first
   * `checkSessionStatus()` over a `PopupTransport` when a host configures one.
   * The iframe is merely where it bites in the DEFAULT configuration.
   */
  bootResume?: Promise<unknown>;
}

export interface CreatePopupBridgeResult {
  bridge: PopupHostBridge;
  pendingConsentStore: PendingConsentStore;
  /**
   * Task 22. The route mounts `<RegisterPrompt store={pendingRegistrationStore} />`
   * so `wallet:hello{intent:'register'}` has a UI to hand off to.
   */
  pendingRegistrationStore: PendingRegistrationStore;
  /**
   * Release auxiliary resources owned by the bridge wiring (currently the
   * cross-tab auth reaction). Call this AFTER `bridge.stop()` on unmount.
   */
  dispose: () => void;
}

/**
 * Construct a fully-wired popup-side bridge. Spec §5 transport + §6.2 routes.
 *
 * Returns the bridge, the `pendingConsentStore` it owns (the route mounts
 * `<ConsentPrompt store={pendingConsentStore} />` to render the modal — Spec
 * §3, Cycle 7), and a `dispose()` that releases the cross-tab reaction this
 * factory registers internally.
 *
 * Caller responsibilities:
 *   - Pass the right `hostOrigin` (typically from URL `?origin=...`).
 *   - Call `bridge.start({ announceReady: { id } })` to begin listening and
 *     announce ready to the opener.
 *   - On unmount: `bridge.stop()` then `dispose()`.
 */
export function createPopupBridge(opts: CreatePopupBridgeOptions): CreatePopupBridgeResult {
  const bridge = new PopupHostBridge({
    hostOrigin: opts.hostOrigin,
    popupWindow: opts.popupWindow,
  });
  const send = (msg: any) => bridge.send(msg);
  const pendingConsentStore = opts.pendingConsentStore ?? new PendingConsentStore();
  const pendingRegistrationStore = opts.pendingRegistrationStore ?? new PendingRegistrationStore();
  // The wallet SPA's own origin — the popup/iframe is served from the wallet
  // origin, so `location.origin` here IS the wallet origin. Used by the
  // requestDelegation handler for its first-party (same-site) consent default.
  const walletOrigin = (globalThis as any).location?.origin ?? '';
  // #74 — the set of origins that auto-approve delegation is explicit
  // per-environment config, not a guess derived from the wallet's hostname.
  const firstPartyOrigins = getFirstPartyOrigins();
  const deps = {
    identity: opts.identity,
    apiClient: opts.apiClient,
    appContextStore: opts.appContextStore,
    applicationsStore: opts.applicationsStore,
    delegationsStore: opts.delegationsStore,
    pendingConsentStore,
    // Task 22 — consumed only by createHelloHandler, and only for
    // `intent: 'register'`.
    pendingRegistrationStore,
    walletOrigin,
    firstPartyOrigins,
    send,
    // Issue #36 — consumed only by createSessionStatusHandler; every other
    // handler ignores it.
    bootResume: opts.bootResume,
  };
  bridge.on('wallet:hello', createHelloHandler(deps));
  bridge.on('wallet:sign', createSignHandler(deps));
  bridge.on('wallet:findApplications', createFindApplicationsHandler(deps));
  bridge.on('wallet:saveApplication', createSaveApplicationHandler(deps));
  bridge.on('wallet:requestDelegation', createRequestDelegationHandler(deps));
  bridge.on('wallet:logout', createLogoutHandler(deps));
  // Task 20: the reply's accountId/email/displayName ride `deps.identity` --
  // same object the handler already reads addresses/credentialId off. No
  // extra wiring needed here beyond passing identity through, same as before.
  bridge.on('wallet:session:status', createSessionStatusHandler(deps));

  // Cross-tab logout dismisses any in-flight consent prompt.
  //
  // Scenario: user opens a delegation consent in tab A, then in tab B clicks
  // "Sign out". The logout broadcasts across tabs; IdentityImpl in tab A
  // clears its vault and flips `isAuthenticated` to false. Without this
  // reaction, tab A's modal would still be open with a now-invalid session —
  // approving would just produce an error (or worse, succeed against the
  // pre-logout JWT cached on the await stack). Auto-deny the prompt so the
  // awaiting handler resolves cleanly and the modal closes.
  //
  // We trigger only on a true → false transition (not on initial false) so
  // that opening the popup logged-out doesn't immediately dismiss anything.
  const disposeAuthReaction = reaction(
    () => opts.identity.isAuthenticated,
    (isAuth, wasAuth) => {
      if (wasAuth && !isAuth && pendingConsentStore.pending) {
        pendingConsentStore.resolveCurrent({ kind: 'deny' });
      }
    },
  );

  return {
    bridge,
    pendingConsentStore,
    pendingRegistrationStore,
    dispose: () => {
      disposeAuthReaction();
      // Task 22. The route is going away, so nobody is left to render the
      // registration prompt or resolve it. Settle it as cancelled rather than
      // leaving `createHelloHandler` awaiting a promise that can no longer be
      // resolved by anything: the host gets a named `user-cancelled` error
      // instead of a request it would only ever lose to a timeout. A no-op
      // when nothing is pending.
      pendingRegistrationStore.resolveCurrent({ kind: 'cancelled' });
    },
  };
}
